import { DOCUMENT, isPlatformBrowser, NgClass } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { CraftXGritProductEntry } from '@core-mkt/interfaces/craft-xgrit-product-entry';
import { GeolocationService } from '@core-mkt/services/geolocation/geolocation.service';
import { ProductService } from '@core-mkt/services/product/product.service';
import { QueryParamDirective } from '@core-mkt/services/query-param/query-param.directive';
import { SchemaService } from '@core-mkt/services/schema/schema.service';
import { CheckoutUrlBuilderService } from '@core-mkt/shared/services/checkout-url-builder/checkout-url-builder.service';
import {
  ThemeParserService,
  type ComponentTheme,
  type ThemeInterface,
} from '@core-mkt/services/theme-parser/theme-parser.service';
import type { ProductItem } from '@core-mkt/shared/components/product-card/product-card/product-card';
import { ProductPillComponent } from '@core-mkt/shared/components/product-pill/product-pill.component';
import { ProductPillService } from '@core-mkt/shared/components/product-pill/product-pill.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import type { StateCourseMapping } from './popular-courses';
import { PopularCourses } from './popular-courses';
import { getPopularCoursesSchema } from './popular-courses.schema';

@Component({
  selector: 'popular-courses',
  templateUrl: './popular-courses.component.html',
  styleUrls: ['./popular-courses.component.scss'],
  standalone: true,
  imports: [FontAwesomeModule, NgClass, ProductPillComponent, QueryParamDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PopularCoursesComponent implements OnInit, OnDestroy {
  @Input() data: PopularCourses;

  private readonly geolocationService = inject(GeolocationService);
  private readonly themeParser = inject(ThemeParserService);
  private readonly ps = inject(ProductService);
  private readonly pillService = inject(ProductPillService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly schemaService = inject(SchemaService);
  private readonly checkoutUrlBuilder = inject(CheckoutUrlBuilderService);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly destroy$ = new Subject<void>();

  readonly faArrowRight = faArrowRight;
  activeMapping: StateCourseMapping | null = null;
  courseOneCheckoutLink: string | null = null;
  courseTwoCheckoutLink: string | null = null;
  theme: ThemeInterface;

  ngOnInit(): void {
    if (!this.data) {
      return;
    }
    const themeInput: ComponentTheme = {
      styles: this.data.styles ?? [],
      columnCount: 0,
      columnDistribution: '',
    };
    this.theme = this.themeParser.getThemeClasses(themeInput);
    this.activeMapping = this.findMapping(this.data.defaultState);
    this.schemaService.register(getPopularCoursesSchema(this.activeMapping, this.document.URL));
    void this.loadCheckoutLinks(this.activeMapping);

    if (isPlatformBrowser(this.platformId)) {
      const stateParam = this.route.snapshot.queryParamMap.get('state');
      if (stateParam) {
        const stateAbbr = this.geolocationService.getStateAbbr(this.toTitleCase(stateParam));
        this.activeMapping =
          this.findMapping(stateAbbr ?? stateParam.toUpperCase()) ?? this.findMapping(this.data.defaultState);
        void this.loadCheckoutLinks(this.activeMapping);
        this.cdr.markForCheck();
        return;
      }

      this.geolocationService.loadLocationCookie();
      this.geolocationService.stateAbbr.pipe(takeUntil(this.destroy$)).subscribe((stateAbbr) => {
        if (stateAbbr) {
          this.activeMapping = this.findMapping(stateAbbr) ?? this.findMapping(this.data.defaultState);
          void this.loadCheckoutLinks(this.activeMapping);
          this.cdr.markForCheck();
        }
      });
    }
  }

  get courseOne(): CraftXGritProductEntry | null {
    return this.activeMapping?.courseOne?.[0] ?? null;
  }

  get courseTwo(): CraftXGritProductEntry | null {
    return this.activeMapping?.courseTwo?.[0] ?? null;
  }

  get courseOneItem(): ProductItem | null {
    if (!this.courseOne) return null;
    return this.toProductItem(
      this.courseOne,
      {
        title: this.activeMapping?.courseOneCardTitle,
        ctaLink: this.activeMapping?.courseOneCtaLink,
      },
      this.courseOneCheckoutLink ?? undefined,
    );
  }

  get courseTwoItem(): ProductItem | null {
    if (!this.courseTwo) return null;
    return this.toProductItem(
      this.courseTwo,
      {
        title: this.activeMapping?.courseTwoCardTitle,
        ctaLink: this.activeMapping?.courseTwoCtaLink,
      },
      this.courseTwoCheckoutLink ?? undefined,
    );
  }

  get courseOneCardBorderClass(): string {
    return this.pillService.getCardBorderClass(this.courseOne?.productPills);
  }

  get courseTwoCardBorderClass(): string {
    return this.pillService.getCardBorderClass(this.courseTwo?.productPills);
  }

  get viewAllCoursesLink(): string | null {
    return this.activeMapping?.viewAllCoursesCtaLink?.trim() || null;
  }

  get viewAllCoursesLabel(): string {
    const abbr = this.activeMapping?.stateAbbr?.toUpperCase();
    if (!abbr) return '';
    const stateName = this.geolocationService.getStateName(abbr);
    return `View all ${stateName || abbr} Courses`;
  }

  get dynamicSectionTitle(): string {
    if (!this.data) return '';
    const baseTitle = (this.data.sectionTitle ?? '').replace(/<[^>]*>/g, '').trim();
    if (!baseTitle) return '';

    return baseTitle;
  }

  toProductItem(
    course: CraftXGritProductEntry,
    overrides?: { title?: string; ctaLink?: string },
    checkoutLink?: string,
  ): ProductItem {
    const cardTitle = overrides?.title?.trim() || course.title || '';
    const overrideLink = overrides?.ctaLink?.trim();
    // The Craft override skips the shared builder, so keep a prod checkout URL in it off non-prod.
    const ctaLink =
      (overrideLink && this.checkoutUrlBuilder.toEnvCheckoutHost(overrideLink)) ||
      checkoutLink ||
      course.productPageUrl ||
      undefined;
    return {
      cardTitle,
      ctaLink,
      ctaLabel: '',
      ctaNewTab: false,
      ctaNofollow: false,
      learnMoreLink: '',
      learnMoreLabel: '',
      learnMoreNewTab: false,
      learnMoreNofollow: false,
      productEntry: [course],
      cardTextColor: '',
      starRating: '',
      starCaption: '',
      textContent: null,
      showTitle: true,
      showSavings: false,
      showPriceText: false,
      topContent: '',
      topBackgroundColor: '',
      topTextColor: '',
      topContentStyles: [],
    };
  }

  private async loadCheckoutLinks(mapping: StateCourseMapping | null): Promise<void> {
    this.courseOneCheckoutLink = null;
    this.courseTwoCheckoutLink = null;
    if (!mapping) return;

    const courseOne = mapping.courseOne?.[0];
    const courseTwo = mapping.courseTwo?.[0];

    await Promise.all([
      courseOne && !mapping.courseOneCtaLink && this.ps.isProductValid(courseOne)
        ? this.ps
            .getProductData(courseOne)
            .then((p) => {
              this.courseOneCheckoutLink = p.apiData?.checkoutLink || null;
            })
            .catch(() => {
              this.courseOneCheckoutLink = null;
            })
        : Promise.resolve(),
      courseTwo && !mapping.courseTwoCtaLink && this.ps.isProductValid(courseTwo)
        ? this.ps
            .getProductData(courseTwo)
            .then((p) => {
              this.courseTwoCheckoutLink = p.apiData?.checkoutLink || null;
            })
            .catch(() => {
              this.courseTwoCheckoutLink = null;
            })
        : Promise.resolve(),
    ]);

    this.cdr.markForCheck();
  }

  private findMapping(stateAbbr: string): StateCourseMapping | null {
    if (!stateAbbr || !this.data?.stateCourseMappings?.length) {
      return null;
    }
    return this.data.stateCourseMappings.find((m) => m.stateAbbr.toUpperCase() === stateAbbr.toUpperCase()) ?? null;
  }

  private toTitleCase(str: string): string {
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
